import {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback
} from 'react';

import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import axios from 'axios';

const CallManager = forwardRef(
  ({
    currentUser,
    selectedUser,
    onIncomingCall,
    onCallEnded,
    onActiveCallUserChange
  }, ref) => {

    // =========================================================
    // REFS
    // =========================================================
    const onActiveCallUserChangeRef =
      useRef(onActiveCallUserChange);
    const clientRef = useRef(null);
    const peerConnectionRef = useRef(null);
    const localStreamRef = useRef(null);

    // Identifiant de l'appel entrant affiché dans le modal.
    const pendingIncomingCallIdRef = useRef(null);

    const pendingIncomingCallerIdRef = useRef(null);

    // Minuteur utilisé pour transformer un appel sans réponse en MISSED.
    const missedCallTimeoutRef = useRef(null);

    // 🧊 ICE candidates reçus avant remoteDescription
    const pendingCandidatesRef = useRef([]);

    // Permet d'éviter de reconnecter STOMP chaque fois
    // que onIncomingCall change dans le parent.
    const onIncomingCallRef = useRef(onIncomingCall);
    const onCallEndedRef = useRef(onCallEnded);

    const callStateRef = useRef({
      isCaller: false,
      inProgress: false,
      accepted: false,
      remoteId: null,
      callType: null,
      callId: null,
      retryCount: 0
    });

    // =========================================================
    // UPDATE CALLBACK REF
    // =========================================================

    useEffect(() => {
      onIncomingCallRef.current = onIncomingCall;
    }, [onIncomingCall]);

    useEffect(() => {
      onCallEndedRef.current = onCallEnded;
    }, [onCallEnded]);

    useEffect(() => {
      onActiveCallUserChangeRef.current =
        onActiveCallUserChange;
    }, [onActiveCallUserChange]);
    // =========================================================
    // STUN SERVERS
    // =========================================================

    const iceServers = [
      {
        urls: 'stun:stun.l.google.com:19302'
      },
      {
        urls: 'stun:stun1.l.google.com:19302'
      },
      {
        urls: 'stun:stun2.l.google.com:19302'
      }
    ];

    // =========================================================
    // CHECK STOMP CONNECTION
    // =========================================================

    const checkClientConnected = useCallback(() => {
      return clientRef.current && clientRef.current.connected;
    }, []);

    // =========================================================
    // WAIT FOR STOMP
    // =========================================================

    const waitForStompConnection = useCallback(() => {
      return new Promise((resolve, reject) => {

        const maxAttempts = 15;
        let attempts = 0;

        const check = () => {

          attempts++;

          if (checkClientConnected()) {
            resolve();
            return;
          }

          if (attempts >= maxAttempts) {
            reject(
              new Error('Could not establish STOMP connection')
            );
            return;
          }

          setTimeout(check, 200);
        };

        check();
      });
    }, [checkClientConnected]);

    // =========================================================
    // PUBLISH STOMP MESSAGE
    // =========================================================

    const publish = useCallback(
      async (destination, body) => {

        try {

          await waitForStompConnection();

          console.log(
            '📤 STOMP SEND:',
            destination,
            body
          );

          clientRef.current.publish({
            destination,
            body: JSON.stringify(body),
            headers: {
              'content-type': 'application/json'
            }
          });

        } catch (error) {

          console.error(
            '❌ STOMP Publish error:',
            error
          );

          throw error;
        }
      },
      [waitForStompConnection]
    );

    // =========================================================
    // CALL HISTORY API
    // =========================================================

    const getAuthConfig = useCallback(() => {
      const token = localStorage.getItem('authToken');

      return {
        headers: {
          Authorization: `Bearer ${token || ''}`
        }
      };
    }, []);

    const startCallHistory = useCallback(
      async (receiverId, type) => {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/calls/start`,
            {
              receiverId,
              callType: String(type).toUpperCase()
            },
            getAuthConfig()
          );

          console.log(
            '✅ Call history created:',
            response.data
          );

          return response.data?.id || null;

        } catch (error) {
          console.error(
            '❌ Unable to create call history:',
            error.response?.data || error
          );

          // Le WebRTC continue même si l'historique rencontre un problème.
          return null;
        }
      },
      [getAuthConfig]
    );

    const updateCallHistory = useCallback(
      async (callId, action) => {
        if (!callId) {
          return null;
        }

        try {
          const response = await axios.patch(
            `${process.env.REACT_APP_API_URL}/api/calls/${callId}/${action}`,
            {},
            getAuthConfig()
          );

          console.log(
            `✅ Call ${callId} updated: ${action}`,
            response.data
          );

          return response.data;

        } catch (error) {
          console.error(
            `❌ Unable to update call ${callId} (${action}):`,
            error.response?.data || error
          );

          return null;
        }
      },
      [getAuthConfig]
    );

    const clearMissedCallTimeout = useCallback(() => {
      if (missedCallTimeoutRef.current) {
        clearTimeout(missedCallTimeoutRef.current);
        missedCallTimeoutRef.current = null;
      }
    }, []);

    // =========================================================
    // GET CAMERA / MICROPHONE
    // =========================================================

    const getMediaStream = async (type) => {

      if (
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        throw new Error(
          'getUserMedia is not available. HTTPS is required.'
        );
      }

      const constraints = {

        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },

        video:
          type === 'video'
            ? {
              width: {
                ideal: 1280
              },
              height: {
                ideal: 720
              },
              frameRate: {
                ideal: 30
              }
            }
            : false
      };

      console.log(
        `🎥 Requesting media for ${type} call`
      );

      const stream =
        await navigator.mediaDevices.getUserMedia(
          constraints
        );

      console.log(
        '🎙️ Audio tracks:',
        stream.getAudioTracks()
      );

      console.log(
        '📹 Video tracks:',
        stream.getVideoTracks()
      );

      if (
        stream.getAudioTracks().length === 0
      ) {
        throw new Error(
          'No audio tracks available'
        );
      }

      if (
        type === 'video' &&
        stream.getVideoTracks().length === 0
      ) {
        throw new Error(
          'No video tracks available'
        );
      }

      return stream;
    };

    // =========================================================
    // ADD PENDING ICE CANDIDATES
    // =========================================================

    const addPendingCandidates =
      useCallback(async () => {

        const peer =
          peerConnectionRef.current;

        if (!peer) {
          console.warn(
            '⚠️ No PeerConnection for pending candidates'
          );
          return;
        }

        if (!peer.remoteDescription) {
          console.warn(
            '⚠️ RemoteDescription not ready'
          );
          return;
        }

        console.log(
          '🧊 Pending ICE candidates:',
          pendingCandidatesRef.current.length
        );

        while (
          pendingCandidatesRef.current.length > 0
        ) {

          const candidate =
            pendingCandidatesRef.current.shift();

          try {

            await peer.addIceCandidate(
              new RTCIceCandidate(candidate)
            );

            console.log(
              '✅ Pending ICE candidate added'
            );

          } catch (error) {

            console.error(
              '❌ Error adding pending ICE:',
              error
            );
          }
        }
      }, []);

    // =========================================================
    // CLEAN CALL
    // =========================================================

    const cleanupCall = useCallback(
      (preservePendingCandidates = false) => {

        console.log('🧹 Cleaning call');

        clearMissedCallTimeout();

        if (peerConnectionRef.current) {

          try {
            peerConnectionRef.current.ontrack =
              null;

            peerConnectionRef.current.onicecandidate =
              null;

            peerConnectionRef.current.onconnectionstatechange =
              null;

            peerConnectionRef.current.oniceconnectionstatechange =
              null;

            peerConnectionRef.current.onicegatheringstatechange =
              null;

            peerConnectionRef.current.onsignalingstatechange =
              null;

            peerConnectionRef.current.close();

          } catch (error) {
            console.error(
              'Peer close error:',
              error
            );
          }

          peerConnectionRef.current = null;
        }

        if (localStreamRef.current) {

          localStreamRef.current
            .getTracks()
            .forEach(track => {

              try {
                track.stop();
              } catch (error) {
                console.error(
                  'Track stop error:',
                  error
                );
              }
            });

          localStreamRef.current = null;
        }

        [
          'localVideo',
          'remoteVideo',
          'remoteAudio'
        ].forEach(id => {

          const element =
            document.getElementById(id);

          if (element) {
            element.srcObject = null;
          }
        });

        // IMPORTANT :
        // Quand le receiver clique "Accepter",
        // il faut conserver les candidates reçus
        // pendant que le modal était affiché.
        if (!preservePendingCandidates) {
          pendingCandidatesRef.current = [];
        }

        pendingIncomingCallIdRef.current = null;
        pendingIncomingCallerIdRef.current = null;

        callStateRef.current = {
          isCaller: false,
          inProgress: false,
          accepted: false,
          remoteId: null,
          callType: null,
          callId: null,
          retryCount: 0
        };
      },
      [clearMissedCallTimeout]
    );

    // =========================================================
    // END / REJECT / MISSED CALL
    // =========================================================

    const endCallInternal =
      useCallback(async () => {

        if (
          !callStateRef.current.inProgress
        ) {
          return;
        }

        const callSnapshot = {
          ...callStateRef.current
        };

        const remoteId =
          callSnapshot.remoteId;

        console.log(
          '📴 Ending call with:',
          remoteId
        );

        clearMissedCallTimeout();

        try {

          /*
           * Avant la réponse, le bouton raccrocher signifie CANCELLED.
           * Après la réponse, il signifie ENDED.
           */
          if (callSnapshot.callId) {
            const action =
              callSnapshot.isCaller &&
                !callSnapshot.accepted
                ? 'cancel'
                : 'end';

            await updateCallHistory(
              callSnapshot.callId,
              action
            );
          }

          if (
            remoteId &&
            clientRef.current?.connected
          ) {

            await publish(
              `/app/call/${remoteId}`,
              {
                from: currentUser.id,
                to: remoteId,
                callId: callSnapshot.callId,
                endCall: true,
                cancelled:
                  callSnapshot.isCaller &&
                  !callSnapshot.accepted
              }
            );
          }

        } catch (error) {

          console.error(
            '❌ Error ending call:',
            error
          );
        }

        cleanupCall(false);

      }, [
        currentUser?.id,
        publish,
        cleanupCall,
        clearMissedCallTimeout,
        updateCallHistory
      ]);

    const rejectIncomingCall =
      useCallback(async payload => {

        const remoteId =
          payload?.from ||
          callStateRef.current.remoteId;

        const callId =
          payload?.callId ||
          pendingIncomingCallIdRef.current ||
          callStateRef.current.callId;

        clearMissedCallTimeout();

        await updateCallHistory(
          callId,
          'reject'
        );

        try {
          if (
            remoteId &&
            clientRef.current?.connected
          ) {
            await publish(
              `/app/call/${remoteId}`,
              {
                from: currentUser.id,
                to: remoteId,
                callId,
                endCall: true,
                rejected: true
              }
            );
          }
        } catch (error) {
          console.error(
            '❌ Error sending call rejection:',
            error
          );
        }

        cleanupCall(false);

        if (onCallEndedRef.current) {
          onCallEndedRef.current();
        }

      }, [
        currentUser?.id,
        publish,
        cleanupCall,
        clearMissedCallTimeout,
        updateCallHistory
      ]);

    const scheduleMissedCallTimeout =
      useCallback((callId, remoteId) => {

        clearMissedCallTimeout();

        /*
         * Sans callId, l'appel WebRTC continue normalement,
         * mais aucun historique ne peut être mis à jour.
         */
        if (!callId) {
          return;
        }

        missedCallTimeoutRef.current =
          setTimeout(async () => {

            const currentCall =
              callStateRef.current;

            const isSameCall =
              String(currentCall.callId) ===
              String(callId);

            if (
              !currentCall.inProgress ||
              !currentCall.isCaller ||
              currentCall.accepted ||
              !isSameCall
            ) {
              return;
            }

            console.log(
              '⏰ No answer after 30 seconds. Marking call as MISSED.'
            );

            await updateCallHistory(
              callId,
              'missed'
            );

            try {
              if (
                remoteId &&
                clientRef.current?.connected
              ) {
                await publish(
                  `/app/call/${remoteId}`,
                  {
                    from: currentUser.id,
                    to: remoteId,
                    callId,
                    endCall: true,
                    missed: true
                  }
                );
              }
            } catch (error) {
              console.error(
                '❌ Error sending missed-call signal:',
                error
              );
            }

            cleanupCall(false);

            if (onCallEndedRef.current) {
              onCallEndedRef.current();
            }

          }, 30000);

      }, [
        currentUser?.id,
        publish,
        cleanupCall,
        clearMissedCallTimeout,
        updateCallHistory
      ]);

    // =========================================================
    // START / ANSWER CALL
    // =========================================================

    const handleCall = async (
      type,
      isCaller,
      payload = null
    ) => {

      try {

        console.log(
          isCaller
            ? `📞 Starting ${type} call`
            : `📲 Answering ${type} call`
        );

        /*
         * IMPORTANT
         *
         * CALLER :
         * nettoyer les anciens candidates.
         *
         * RECEIVER :
         * conserver les ICE candidates qui sont
         * arrivés pendant que le CallModal était affiché.
         */
        cleanupCall(!isCaller);

        if (
          isCaller &&
          !selectedUser?.id
        ) {
          throw new Error(
            'No selected user'
          );
        }

        if (
          !isCaller &&
          !payload?.from
        ) {
          throw new Error(
            'Incoming call payload is invalid'
          );
        }

        const remoteId =
          isCaller
            ? selectedUser.id
            : payload.from;

        if (
          onActiveCallUserChangeRef.current
        ) {

          if (isCaller) {

            onActiveCallUserChangeRef.current(
              selectedUser
            );

          } else {

            onActiveCallUserChangeRef.current({
              id: payload.from,

              name:
                payload.callerName ||
                payload.fromUserName ||
                payload.name ||
                'User',

              profilePicture:
                payload.callerImage ||
                payload.fromUserImage ||
                null
            });

          }
        }

        let callId =
          isCaller
            ? null
            : payload?.callId || null;

        callStateRef.current = {
          isCaller,
          inProgress: true,
          accepted: !isCaller,
          remoteId,
          callType: type,
          callId,
          retryCount: 0
        };

        if (!isCaller) {
          pendingIncomingCallIdRef.current = null;
        }

        // =====================================================
        // MICROPHONE / CAMERA
        // =====================================================

        const stream =
          await getMediaStream(type);

        localStreamRef.current =
          stream;

        /*
         * On crée l'historique seulement après l'autorisation
         * du microphone/de la caméra, pour éviter les faux appels.
         */
        if (isCaller) {
          callId = await startCallHistory(
            remoteId,
            type
          );

          callStateRef.current.callId =
            callId;
        }

        const localVideo =
          document.getElementById(
            'localVideo'
          );

        if (localVideo) {

          localVideo.srcObject =
            stream;

          // IMPORTANT :
          // ne pas entendre son propre microphone.
          localVideo.muted = true;

          localVideo.playsInline = true;

          try {
            await localVideo.play();
          } catch (error) {
            console.warn(
              '⚠️ Local video autoplay:',
              error
            );
          }
        }

        // =====================================================
        // CREATE PEER CONNECTION
        // =====================================================

        const peer =
          new RTCPeerConnection({
            iceServers
          });

        peerConnectionRef.current =
          peer;

        console.log(
          '✅ RTCPeerConnection created'
        );

        // =====================================================
        // REMOTE AUDIO / VIDEO
        // =====================================================

        peer.ontrack = async event => {

          console.log(
            '🔊🔁 Remote track received:',
            event.track.kind,
            event.streams
          );

          let remoteStream =
            event.streams?.[0];

          // Certains browsers peuvent
          // retourner event.streams vide.
          if (!remoteStream) {

            remoteStream =
              new MediaStream();

            remoteStream.addTrack(
              event.track
            );
          }

          const remoteVideo =
            document.getElementById(
              'remoteVideo'
            );

          const remoteAudio =
            document.getElementById(
              'remoteAudio'
            );

          /*
           * VIDEO
           *
           * Si remoteAudio existe,
           * remoteVideo est muted pour éviter
           * d'entendre le son deux fois.
           */
          if (
            remoteVideo &&
            type === 'video'
          ) {

            if (
              remoteVideo.srcObject !==
              remoteStream
            ) {
              remoteVideo.srcObject =
                remoteStream;
            }

            remoteVideo.autoplay = true;
            remoteVideo.playsInline = true;

            remoteVideo.muted =
              !!remoteAudio;

            try {

              await remoteVideo.play();

              console.log(
                '▶️ Remote video started'
              );

            } catch (error) {

              console.warn(
                '⚠️ Remote video play error:',
                error
              );
            }
          }

          // ===================================================
          // AUDIO
          // ===================================================

          if (remoteAudio) {

            if (
              remoteAudio.srcObject !==
              remoteStream
            ) {
              remoteAudio.srcObject =
                remoteStream;
            }

            remoteAudio.autoplay = true;
            remoteAudio.muted = false;
            remoteAudio.volume = 1;

            try {

              await remoteAudio.play();

              console.log(
                '🔊 Remote audio started'
              );

            } catch (error) {

              console.warn(
                '⚠️ Remote audio autoplay blocked:',
                error
              );
            }

          } else if (
            type === 'audio'
          ) {

            console.warn(
              '❌ remoteAudio element not found in DOM'
            );
          }
        };

        // =====================================================
        // ICE CANDIDATES
        // =====================================================

        peer.onicecandidate =
          async event => {

            if (!event.candidate) {

              console.log(
                '🧊 ICE gathering finished'
              );

              return;
            }

            console.log(
              '🧊 Local ICE candidate:',
              event.candidate.type
            );

            try {

              await publish(
                `/app/call/${remoteId}`,
                {
                  from: currentUser.id,
                  to: remoteId,
                  callId,
                  candidate:
                    event.candidate.toJSON
                      ? event.candidate.toJSON()
                      : event.candidate
                }
              );

            } catch (error) {

              console.error(
                '❌ ICE candidate publish error:',
                error
              );
            }
          };

        // =====================================================
        // WEBRTC STATE LOGS
        // =====================================================

        peer.onconnectionstatechange =
          () => {

            console.log(
              '🔗 Connection state:',
              peer.connectionState
            );

            if (
              peer.connectionState ===
              'connected'
            ) {

              console.log(
                '✅✅ WEBRTC CONNECTED ✅✅'
              );
            }

            /*
             * IMPORTANT :
             * NE PAS couper immédiatement
             * sur "disconnected".
             *
             * disconnected peut être temporaire.
             */
            if (
              peer.connectionState ===
              'failed'
            ) {

              console.error(
                '❌ WEBRTC CONNECTION FAILED'
              );
            }

            if (
              peer.connectionState ===
              'closed'
            ) {

              console.log(
                '📴 WebRTC connection closed'
              );
            }
          };

        peer.oniceconnectionstatechange =
          () => {

            console.log(
              '🧊 ICE connection state:',
              peer.iceConnectionState
            );

            if (
              peer.iceConnectionState ===
              'connected' ||
              peer.iceConnectionState ===
              'completed'
            ) {

              console.log(
                '✅ ICE CONNECTION OK'
              );
            }

            if (
              peer.iceConnectionState ===
              'failed'
            ) {

              console.error(
                '❌ ICE CONNECTION FAILED'
              );
            }
          };

        peer.onicegatheringstatechange =
          () => {

            console.log(
              '🧊 ICE gathering state:',
              peer.iceGatheringState
            );
          };

        peer.onsignalingstatechange =
          () => {

            console.log(
              '📡 Signaling state:',
              peer.signalingState
            );
          };

        // =====================================================
        // ADD LOCAL TRACKS
        // =====================================================

        stream
          .getTracks()
          .forEach(track => {

            console.log(
              '➕ Adding local track:',
              track.kind
            );

            peer.addTrack(
              track,
              stream
            );
          });

        // =====================================================
        // CALLER
        // =====================================================

        if (isCaller) {

          console.log(
            '📞 Creating OFFER'
          );

          const offer =
            await peer.createOffer({
              offerToReceiveAudio: true,
              offerToReceiveVideo:
                type === 'video'
            });

          await peer.setLocalDescription(
            offer
          );

          console.log(
            '✅ Local OFFER created'
          );

          await publish(
            `/app/call/${remoteId}`,
            {
              from: currentUser.id,
              to: remoteId,
              callId,

              sdp:
                peer.localDescription.sdp,

              type
            }
          );

          console.log(
            '📤 OFFER sent to:',
            remoteId
          );

          scheduleMissedCallTimeout(
            callId,
            remoteId
          );

        }

        // =====================================================
        // RECEIVER
        // =====================================================

        else {

          console.log(
            '📥 Setting incoming OFFER'
          );

          await peer.setRemoteDescription(
            new RTCSessionDescription({
              type: 'offer',
              sdp: payload.sdp
            })
          );

          console.log(
            '✅ Remote OFFER added'
          );

          /*
           * Maintenant remoteDescription existe.
           *
           * On peut ajouter tous les ICE candidates
           * reçus pendant que le modal était affiché.
           */
          await addPendingCandidates();

          console.log(
            '📲 Creating ANSWER'
          );

          const answer =
            await peer.createAnswer();

          await peer.setLocalDescription(
            answer
          );

          console.log(
            '✅ Local ANSWER created'
          );

          await publish(
            `/app/call/${remoteId}`,
            {
              from: currentUser.id,
              to: remoteId,
              callId,

              sdp:
                peer.localDescription.sdp,

              type,

              answer: true
            }
          );

          console.log(
            '📤 ANSWER sent to:',
            remoteId
          );

          await updateCallHistory(
            callId,
            'answer'
          );
        }

      } catch (error) {

        console.error(
          '❌ ERROR IN CALL:',
          error
        );

        const failedCall = {
          ...callStateRef.current
        };

        if (
          failedCall.callId &&
          failedCall.isCaller &&
          !failedCall.accepted
        ) {
          await updateCallHistory(
            failedCall.callId,
            'cancel'
          );
        }

        // Ici on nettoie directement
        // sans dépendre de connectionState.
        cleanupCall(false);
      }
    };

    // =========================================================
    // EXPOSE FUNCTIONS TO PARENT
    // =========================================================

    useImperativeHandle(
      ref,
      () => ({

        isInCall: () =>
          callStateRef.current.inProgress,

        endCall:
          endCallInternal,

        startCall: async type => {

          await handleCall(
            type,
            true
          );
        },

        answerCall:
          async payload => {

            await handleCall(
              payload.type,
              false,
              payload
            );
          },

        rejectCall:
          async payload => {

            await rejectIncomingCall(
              payload
            );
          },

        toggleMute: () => {

          if (
            !localStreamRef.current
          ) {
            return false;
          }

          const audioTracks =
            localStreamRef.current
              .getAudioTracks();

          if (
            audioTracks.length === 0
          ) {
            return false;
          }

          const shouldEnable =
            !audioTracks[0].enabled;

          audioTracks.forEach(
            track => {
              track.enabled =
                shouldEnable;
            }
          );

          console.log(
            shouldEnable
              ? '🎙️ Microphone ON'
              : '🔇 Microphone OFF'
          );

          return shouldEnable;
        }
      })
    );

    // =========================================================
    // STOMP / SOCKJS CONNECTION
    // =========================================================

    useEffect(() => {

      if (!currentUser?.id) {
        return;
      }

      const token =
        localStorage.getItem(
          'authToken'
        );

      console.log(
        '🔌 Starting STOMP for user:',
        currentUser.id
      );

      const client =
        new Client({

          /*
           * IMPORTANT FIX:
           *
           * Toujours créer un NOUVEAU SockJS
           * lorsque STOMP reconnecte.
           */
          webSocketFactory: () => {

            console.log(
              '🔌 Creating new SockJS connection'
            );

            return new SockJS(
              `${process.env.REACT_APP_API_URL}/ws?token=${encodeURIComponent(
                token || ''
              )}`
            );
          },

          reconnectDelay: 5000,

          heartbeatIncoming: 4000,
          heartbeatOutgoing: 4000,

          debug: message => {

            // Tu peux commenter ce console.log
            // quand tout fonctionne.
            console.log(
              'STOMP:',
              message
            );
          },

          // ===================================================
          // CONNECTED
          // ===================================================

          onConnect: () => {

            console.log(
              '✅ STOMP CONNECTED:',
              currentUser.id
            );

            client.subscribe(
              `/topic/call/${currentUser.id}`,
              async message => {

                try {

                  const payload =
                    JSON.parse(
                      message.body
                    );

                  console.log(
                    '📥 CALL MESSAGE:',
                    payload
                  );

                  /*
                   * Ignore notre propre message
                   */
                  if (
                    String(payload.from) ===
                    String(currentUser.id)
                  ) {
                    return;
                  }

                  // ============================================
                  // END CALL
                  // ============================================

                  if (payload.endCall) {
                    console.log(
                      payload.rejected
                        ? '📵 Remote user rejected the call'
                        : payload.missed
                          ? '⏰ Call ended without an answer'
                          : '📴 Remote user ended call'
                    );

                    const activeCall =
                      callStateRef.current;

                    const sameActiveUser =
                      activeCall.remoteId != null &&
                      String(activeCall.remoteId) ===
                      String(payload.from);

                    const samePendingCaller =
                      pendingIncomingCallerIdRef.current != null &&
                      String(
                        pendingIncomingCallerIdRef.current
                      ) === String(payload.from);

                    const sameCallId =
                      payload.callId != null &&
                      (
                        String(activeCall.callId) ===
                        String(payload.callId) ||
                        String(
                          pendingIncomingCallIdRef.current
                        ) === String(payload.callId)
                      );

                    if (
                      sameActiveUser ||
                      samePendingCaller ||
                      sameCallId
                    ) {
                      clearMissedCallTimeout();
                      cleanupCall(false);

                      if (onCallEndedRef.current) {
                        onCallEndedRef.current(payload);
                      }
                    }

                    return;
                  }

                  // ============================================
                  // SDP OFFER / ANSWER
                  // ============================================

                  if (payload.sdp) {

                    // ==========================================
                    // INCOMING OFFER
                    // ==========================================

                    if (
                      !payload.answer
                    ) {

                      console.log(
                        '📞 Incoming OFFER from:',
                        payload.from,
                        'callId:',
                        payload.callId
                      );

                      if (
                        !callStateRef.current
                          .inProgress
                      ) {

                        pendingIncomingCallIdRef.current =
                          payload.callId || null;

                        pendingIncomingCallerIdRef.current =
                          payload.from || null;

                        if (
                          onIncomingCallRef.current
                        ) {

                          onIncomingCallRef.current(
                            payload
                          );
                        }

                      } else {

                        console.warn(
                          '⚠️ Already in another call'
                        );
                      }

                      return;
                    }

                    // ==========================================
                    // ANSWER RECEIVED BY CALLER
                    // ==========================================

                    if (
                      payload.answer &&
                      callStateRef.current
                        .isCaller
                    ) {

                      const peer =
                        peerConnectionRef.current;

                      if (!peer) {

                        console.error(
                          '❌ PeerConnection missing when ANSWER arrived'
                        );

                        return;
                      }

                      console.log(
                        '📥 ANSWER received'
                      );

                      /*
                       * Évite setRemoteDescription
                       * deux fois.
                       */
                      if (
                        !peer.remoteDescription
                      ) {

                        await peer.setRemoteDescription(
                          new RTCSessionDescription({
                            type: 'answer',
                            sdp: payload.sdp
                          })
                        );

                        console.log(
                          '✅ Remote ANSWER added'
                        );
                      }

                      /*
                       * Ajouter maintenant les ICE
                       * candidates qui attendaient.
                       */
                      await addPendingCandidates();

                      clearMissedCallTimeout();

                      callStateRef.current.accepted =
                        true;

                      console.log(
                        '✅ Call accepted'
                      );

                      return;
                    }
                  }

                  // ============================================
                  // ICE CANDIDATE
                  // ============================================

                  if (
                    payload.candidate
                  ) {

                    console.log(
                      '🧊 Remote ICE candidate received'
                    );

                    const peer =
                      peerConnectionRef.current;

                    /*
                     * Si PeerConnection existe ET
                     * remoteDescription existe :
                     * ajouter directement.
                     */
                    if (
                      peer &&
                      peer.remoteDescription
                    ) {

                      try {

                        await peer.addIceCandidate(
                          new RTCIceCandidate(
                            payload.candidate
                          )
                        );

                        console.log(
                          '✅ Remote ICE candidate added'
                        );

                      } catch (error) {

                        console.error(
                          '❌ addIceCandidate error:',
                          error
                        );
                      }

                    }

                    /*
                     * Sinon :
                     * conserver le candidate.
                     *
                     * C'est très important lorsque
                     * le receiver n'a pas encore
                     * cliqué sur "Accepter".
                     */
                    else {

                      console.log(
                        '⏳ ICE candidate saved for later'
                      );

                      pendingCandidatesRef.current.push(
                        payload.candidate
                      );

                      console.log(
                        '🧊 Candidates waiting:',
                        pendingCandidatesRef.current
                          .length
                      );
                    }
                  }

                } catch (error) {

                  console.error(
                    '❌ Error processing call message:',
                    error
                  );
                }
              }
            );
          },

          // ===================================================
          // CONNECTION ERRORS
          // ===================================================

          onStompError: frame => {

            console.error(
              '❌ STOMP ERROR:',
              frame.headers?.message
            );

            console.error(
              frame.body
            );
          },

          onWebSocketError: error => {

            console.error(
              '❌ WebSocket error:',
              error
            );
          },

          onWebSocketClose: event => {

            console.warn(
              '⚠️ WebSocket closed:',
              event.code,
              event.reason
            );

            /*
             * IMPORTANT :
             *
             * On ne tue PAS immédiatement
             * l'appel WebRTC.
             *
             * Une connexion WebRTC déjà établie
             * peut continuer même si STOMP
             * reconnecte.
             */
          },

          onDisconnect: () => {

            console.log(
              '🔌 STOMP disconnected'
            );
          }
        });

      clientRef.current =
        client;

      client.activate();

      // =======================================================
      // COMPONENT UNMOUNT
      // =======================================================

      return () => {

        console.log(
          '🧹 CallManager unmount'
        );

        cleanupCall(false);

        if (clientRef.current) {

          try {

            clientRef.current.deactivate();

          } catch (error) {

            console.error(
              'STOMP deactivate error:',
              error
            );
          }
        }

        clientRef.current =
          null;
      };

    }, [
      currentUser?.id,
      cleanupCall,
      addPendingCandidates,
      clearMissedCallTimeout
    ]);

    return null;
  }
);

CallManager.displayName =
  'CallManager';

export default CallManager;